"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.entityLength = entityLength;
exports.shortenEntities = shortenEntities;
exports.shortenEntity = shortenEntity;
exports.splitEntity = splitEntity;
exports.traverseEntities = traverseEntities;
var _vector = require("./vector");
var _getPointTangent = require("./getPointTangent");
/**
 * Shortens the last entity(ies) in an array of entities by the specified amount. Used to create microjoints or to shorten the first pass of a lead-in.
 * @param entities The entities to shorten
 * @param delta The distance to shorten the entities by
 * @returns A new array of entities that have been shortened.
 */
function shortenEntities(entities, delta) {
  let shortenedEntities = [...entities];
  let eIdx = shortenedEntities.length - 1;
  while (true) {
    if (eIdx < 0) {
      return [];
    }
    let eLength = entityLength(shortenedEntities[eIdx]);
    if (delta >= eLength) {
      shortenedEntities.splice(eIdx, 1);
      delta -= eLength;
      eIdx--;
    } else {
      let newEntity = shortenEntity(shortenedEntities[eIdx], delta);
      shortenedEntities[eIdx] = newEntity;
      return shortenedEntities;
    }
  }
}

/**
 * Shortens a single entity.
 * @param entity The entity to shorten
 * @param delta How much to shorten it by
 */
function shortenEntity(entity, delta) {
  const e = entity;
  switch (e.type) {
    case 'LINE':
      let t = delta / (0, _vector.norm)((0, _vector.sub)(e.start, e.end));
      if (e.reversed) {
        let shortPoint = (0, _vector.add)((0, _vector.mul)(e.start, 1 - t), (0, _vector.mul)(e.end, t));
        return {
          ...e,
          type: 'LINE',
          start: shortPoint,
          end: e.end,
          reversed: true
        };
      } else {
        let shortPoint = (0, _vector.add)((0, _vector.mul)(e.start, t), (0, _vector.mul)(e.end, 1 - t));
        return {
          ...e,
          type: 'LINE',
          start: e.start,
          end: shortPoint,
          reversed: false
        };
      }
    case 'ARC':
      // Oh no oh no oh no
      let deltaAngle = delta / e.r;
      if (e.reversed) {
        let newArc = {
          ...e,
          type: 'ARC',
          x: e.x,
          y: e.y,
          r: e.r,
          startAngle: e.startAngle + deltaAngle,
          endAngle: e.endAngle,
          reversed: true
        };
        while (newArc.startAngle >= Math.PI * 2) {
          newArc.startAngle -= Math.PI * 2;
          newArc.endAngle -= Math.PI * 2;
        }
        return newArc;
      } else {
        return {
          ...e,
          type: 'ARC',
          x: e.x,
          y: e.y,
          r: e.r,
          startAngle: e.startAngle,
          endAngle: e.endAngle - deltaAngle,
          reversed: false
        };
      }
    default:
      throw new Error('Unsupported entity type: ' + e.type);
  }
}
function entityLength(entity) {
  const e = entity;
  switch (e.type) {
    case 'LINE':
      return (0, _vector.norm)((0, _vector.sub)(e.start, e.end));
    case 'ARC':
      let angleSubtended = e.endAngle - e.startAngle;
      while (angleSubtended < 0) angleSubtended += 2 * Math.PI;
      while (angleSubtended > 2 * Math.PI + 1e-14) angleSubtended -= 2 * Math.PI; // The +1e-14 is a hack to support whole circle arcs
      return e.r * angleSubtended;
    case 'CIRCLE':
      return e.r * Math.PI * 2;
    default:
      throw new Error('Unsupported entity type: ' + e.type);
  }
}

/**
 * Traverse a distance along several entities and return the final location. traverseEntities considers only the length
 * of each entity. For legacy reasons, by default it does NOT account for an entity's `reversed` property, but this can
 * be changed with the `correctReversedEntities` parameter. The function does not check to see if adjacent entities are
 * connected. 
 * @param entities Array of entities to traverse
 * @param startingIndex Starting index of the entity to begin traversing from
 * @param startingParametric Parametric location on the starting entity to begin traversing. (The parametric location
 * will only be corrected for the entity's `reversed` property if correctReversedEntities is true.)
 * @param distance Distance to traverse. Can be positive or negative.
 * @param correctReversedEntities If true, will correct for the `reversed` property of each entity. Most of the time,
 * this is what you will want. However, the default is false to preserve compatibility with older versions.
 * @returns Array whose first item is the index of the ending entity and whose second item is the parametric location on
 * that entity. (The parametric location is only corrected for the entity's `reversed` property if
 * correctReversedEntities is true.)
 */
function traverseEntities(entities, startingIndex, startingParametric, distance, correctReversedEntities = false) {
  let direction = distance < 0 ? -1 : 1;
  distance = Math.abs(distance);
  if (direction === -1) {
    startingParametric = 1 - startingParametric;
  }
  if (correctReversedEntities && entities[startingIndex].reversed) {
    startingParametric = 1 - startingParametric;
  }
  let currDistToMove = distance;
  let currEntityIdx = startingIndex;
  let currEntityParam = startingParametric;
  while (currDistToMove > 0) {
    let eLen = entityLength(entities[currEntityIdx]);
    let nextEntityParam = currEntityParam + currDistToMove / eLen;
    if (nextEntityParam < 1) {
      currEntityParam = nextEntityParam;
      currDistToMove = 0;
    } else {
      currDistToMove -= eLen * (1 - currEntityParam);
      currEntityParam = 0;
      currEntityIdx = (currEntityIdx + direction + entities.length) % entities.length;
    }
  }
  if (direction === -1) {
    currEntityParam = 1 - currEntityParam;
  }
  if (correctReversedEntities && entities[currEntityIdx].reversed) {
    currEntityParam = 1 - currEntityParam;
  }
  return [currEntityIdx, currEntityParam];
}

/**
 * Splits the entity into one or two entities at the parametric location given by t.
 * @param entity Entity to split
 * @param t Parametric location on the entity to make the cut (not corrected for the entity's `reversed` property)
 * @returns An array of one or two entities formed by cutting the entity at the given point. A circle becomes an
 * arc, a line becomes two lines, an arc becomes two arcs. The array order of the entities will depend on the original
 * entity's `reversed` property, so that splitting an entity within a contour will not change the monotonicity of the entities in that contour.
 */
function splitEntity(entity, t) {
  const e = entity;
  const cutPoint = (0, _getPointTangent.getPointTangentOfEntity)(e, t)[0];
  switch (e.type) {
    case 'LINE':
      {
        let e1 = {
          ...e,
          type: 'LINE',
          start: e.start,
          end: cutPoint,
          reversed: e.reversed
        };
        let e2 = {
          ...e,
          type: 'LINE',
          start: cutPoint,
          end: e.end,
          reversed: e.reversed
        };
        if (e.reversed) {
          return [e2, e1];
        } else {
          return [e1, e2];
        }
      }
    case 'ARC':
      {
        // I always mess up the angles. Let's see....

        const cutAngle = e.startAngle * (1 - t) + e.endAngle * t;
        let e1 = {
          ...e,
          type: 'ARC',
          x: e.x,
          y: e.y,
          r: e.r,
          startAngle: e.startAngle,
          endAngle: cutAngle,
          reversed: e.reversed
        };
        let e2 = {
          ...e,
          type: 'ARC',
          x: e.x,
          y: e.y,
          r: e.r,
          startAngle: cutAngle,
          endAngle: e.endAngle,
          reversed: e.reversed
        };

        // It's now possible for one of the arcs' startAngles to be > 2 * Math.PI
        while (e1.startAngle >= Math.PI * 2) {
          e1.startAngle -= Math.PI * 2;
          e1.endAngle -= Math.PI * 2;
        }
        while (e2.startAngle >= Math.PI * 2) {
          e2.startAngle -= Math.PI * 2;
          e2.endAngle -= Math.PI * 2;
        }
        if (e.reversed) {
          return [e2, e1];
        } else {
          return [e1, e2];
        }
      }
    case 'CIRCLE':
      {
        // console.log(cutPoint)
        let cutAngle = (0, _vector.atan2)((0, _vector.sub)(cutPoint, e));
        while (cutAngle < 0) cutAngle += 2 * Math.PI;
        while (cutAngle >= 2 * Math.PI) cutAngle -= 2 * Math.PI;
        let e1 = {
          ...e,
          type: 'ARC',
          x: e.x,
          y: e.y,
          r: e.r,
          startAngle: cutAngle,
          endAngle: cutAngle + 2 * Math.PI,
          reversed: e.reversed
        };
        return [e1];
      }
    default:
      throw new Error('Unsupported entity type: ' + e.type);
  }
}